import React, { useState } from 'react';

import { ChevronLeft, ChevronRight, List, Loader2, Search } from 'lucide-react';

import useImportRelativityList from '@/api/mutations/useImportRelativityList';
import useGetRelativityDocumentCount from '@/api/queries/useGetRelativityDocumentCount';
import useGetRelativityLists from '@/api/queries/useGetRelativityLists';
import { RelativityListType, RelativityWorkspaceType } from '@/api/types/backend-dto-types';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/new-button';

interface RelativityListsModalProps {
  workspace: RelativityWorkspaceType;
  caseId: string;
  onSelectList: (list: RelativityListType) => void;
  onBack: () => void;
  onClose: () => void;
}

interface ListItemProps {
  list: RelativityListType;
  workspaceId: number;
  isSelected: boolean;
  onSelect: (list: RelativityListType) => void;
}

const ListItem: React.FC<ListItemProps> = ({ list, workspaceId, isSelected, onSelect }) => {
  const { data: documentCount, isLoading: isLoadingCount } = useGetRelativityDocumentCount(
    workspaceId,
    list.artifactId,
  );

  return (
    <div
      className={`group cursor-pointer rounded-lg border p-4 transition-all duration-200 hover:shadow-sm ${
        isSelected
          ? 'border-brandSecondary bg-brandSecondary/10 ring-brandSecondary ring-1 shadow-sm'
          : 'hover:border-brandSecondary/30 hover:bg-brandSecondary/5 border-gray-200'
      }`}
      onClick={() => onSelect(list)}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center space-x-3">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
              isSelected ? 'bg-brandSecondary/20' : 'group-hover:bg-brandSecondary/15 bg-gray-100'
            }`}
          >
            <List
              className={`h-5 w-5 ${
                isSelected ? 'text-brandSecondary' : 'group-hover:text-brandSecondary text-gray-600'
              }`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className={`truncate text-sm font-medium ${
                isSelected ? 'text-brandPrimary' : 'group-hover:text-brandPrimary text-gray-900'
              }`}
              title={list.name}
            >
              {list.name}
            </h3>
            <p className="truncate text-sm text-gray-500" title={`ID: ${list.artifactId}`}>
              ID: {list.artifactId}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          {isLoadingCount ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : (
            <div className="text-sm">
              <span className="font-medium text-gray-900">{documentCount?.count?.toLocaleString() ?? '0'}</span>
              <span className="ml-1 text-gray-500">{documentCount?.count === 1 ? 'file' : 'files'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RelativityListsModal: React.FC<RelativityListsModalProps> = ({ workspace, caseId, onBack, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedList, setSelectedList] = useState<RelativityListType | null>(null);
  const pageSize = 5;

  // Get document count for selected list to show in button
  const { data: selectedListDocumentCount } = useGetRelativityDocumentCount(
    workspace.artifactId,
    selectedList?.artifactId ?? 0,
    !!selectedList,
  );

  // Import mutation
  const importMutation = useImportRelativityList(caseId);

  // Note: Search is immediate for better UX, but could be debounced if needed

  const {
    data: listsData,
    isLoading,
    error,
  } = useGetRelativityLists({
    workspaceId: workspace.artifactId,
    page: currentPage,
    pageSize,
    search: searchQuery,
  });

  const handleSelectList = (list: RelativityListType) => {
    setSelectedList(list);
  };

  const handleConfirmSelection = async () => {
    if (selectedList) {
      try {
        const result = await importMutation.mutateAsync({
          workspaceId: workspace.artifactId,
          listId: selectedList.artifactId,
          caseId,
          listName: selectedList.name,
        });

        onClose();
      } catch (error) {
        console.error('Failed to start import:', error);
        // Error is handled by the mutation's onError callback
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    setSelectedList(null);
  };

  const totalPages = Math.ceil((listsData?.totalCount || 0) / pageSize);
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div className="bg-white px-6 py-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Select Relativity List</h2>
        <p className="mt-1 text-sm text-gray-600">Choose a list to import all documents from.</p>
      </div>

      {/* Search and Pagination */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search lists..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Inline Pagination */}
        {listsData && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!canPrevious}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!canNext}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="min-h-[400px]">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="text-brandSecondary h-8 w-8 animate-spin" />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Failed to load lists</h3>
                <p className="mt-1 text-sm text-red-700">
                  {String(error instanceof Error ? error.message : 'An unknown error occurred')}
                </p>
              </div>
            </div>
          </div>
        )}

        {listsData && listsData.lists.length === 0 && !isLoading && (
          <div className="py-20 text-center">
            <List className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? 'No matching lists' : 'No lists found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'Try adjusting your search terms.' : 'This workspace does not contain any lists.'}
            </p>
          </div>
        )}

        {listsData && listsData.lists.length > 0 && (
          <div className="space-y-3">
            {listsData.lists.map((list) => (
              <ListItem
                key={list.artifactId}
                list={list}
                workspaceId={workspace.artifactId}
                isSelected={selectedList?.artifactId === list.artifactId}
                onSelect={handleSelectList}
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
        <div className="flex items-center text-sm text-gray-500">
          {selectedList && <span className="font-medium text-gray-700">Selected: {selectedList.name}</span>}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          {selectedList && (
            <Button variant="primary" onClick={handleConfirmSelection} disabled={importMutation.isLoading}>
              {importMutation.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting Import...
                </>
              ) : (
                `Import ${selectedListDocumentCount?.count?.toLocaleString() ?? '0'} ${selectedListDocumentCount?.count === 1 ? 'File' : 'Files'}`
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelativityListsModal;
